function convertSingleQuotesToDouble(str) {
  // This function intelligently converts single quotes to double quotes
  // only when they're used as JSON string delimiters, not as apostrophes
  // Also handles mixed quote styles like: {'name': \"Roman's Birthday\"}

  let result = '';
  let i = 0;

  // First pass: handle the pattern where values use escaped double quotes: \"value\"
  // Convert: {'key': \"value\"} to {'key': "value"}
  let preprocessed = '';
  let inSingleQuotedContext = false;

  for (let j = 0; j < str.length; j++) {
    const char = str[j];
    const nextChar = j < str.length - 1 ? str[j + 1] : null;
    const prevChar = j > 0 ? str[j - 1] : null;

    if (char === `'` && prevChar !== '\\') { // eslint-disable-line quotes
      // Track when we're between single quotes (JSON key/value context)
      if (!inSingleQuotedContext) {
        // Check if this starts a key or value
        let k = j - 1;
        while (k >= 0 && /\s/.test(str[k])) k--;
        if (k < 0 || str[k] === '{' || str[k] === ',' || str[k] === ':' || str[k] === '[') {
          inSingleQuotedContext = true;
        }
      } else {
        // Check if this ends a key or value
        let k = j + 1;
        while (k < str.length && /\s/.test(str[k])) k++;
        if (k >= str.length || str[k] === ':' || str[k] === ',' || str[k] === '}' || str[k] === ']') {
          inSingleQuotedContext = false;
        }
      }
      preprocessed += char;
    } else if (char === '\\' && nextChar === '"') {
      if (!inSingleQuotedContext) {
        // This is an escaped double quote that starts/ends a value
        // Remove the backslash
        preprocessed += '"';
        j++; // Skip the next quote
      } else {
        // Keep as is inside single-quoted context
        preprocessed += char;
      }
    } else if (char === '\\' && nextChar === '\\') {
      // Handle \\' pattern
      const lookAhead2 = j < str.length - 2 ? str[j + 2] : null;
      const lookAhead3 = j < str.length - 3 ? str[j + 3] : null;

      if (lookAhead2 === '\\' && lookAhead3 === `'`) { // eslint-disable-line quotes
        // \\' pattern - should just be an apostrophe
        preprocessed += `'`; // eslint-disable-line quotes
        j += 3; // Skip all 4 characters total
      } else {
        // Regular double backslash
        preprocessed += '\\\\';
        j++; // Skip next backslash
      }
    } else {
      preprocessed += char;
    }
  }

  // Second pass: convert single quotes to double quotes for JSON delimiters
  let inString = false;
  let stringDelimiter = null;

  for (i = 0; i < preprocessed.length; i++) {
    const char = preprocessed[i];
    // const nextChar = i < preprocessed.length - 1 ? preprocessed[i + 1] : null;
    // const prevChar = i > 0 ? preprocessed[i - 1] : null;

    // Handle quotes
    if (char === '"' || char === '\'') {
      if (!inString) {
        // Starting a string
        inString = true;
        stringDelimiter = char;
        result += '"'; // Always use double quotes for JSON
      } else if (char === stringDelimiter) {
        // Check if this is really the closing delimiter
        let isClosingDelimiter = false;

        // Skip whitespace to check what comes after
        let j = i + 1;
        while (j < preprocessed.length && /\s/.test(preprocessed[j])) {
          j++;
        }
        const nextNonWhitespace = j < preprocessed.length ? preprocessed[j] : null;

        if (nextNonWhitespace === null || nextNonWhitespace === ','
            || nextNonWhitespace === '}' || nextNonWhitespace === ']'
            || nextNonWhitespace === ':') {
          isClosingDelimiter = true;
        }

        if (isClosingDelimiter) {
          // Ending the string
          inString = false;
          stringDelimiter = null;
          result += '"'; // Always use double quotes for JSON
        } else {
          // It's an apostrophe inside the string, keep as-is
          result += char;
        }
      } else {
        // Different quote type inside string, keep as-is
        result += char;
      }
    } else {
      result += char;
    }
  }

  return result;
}

function parseIncompleteJSON(incompleteJsonString) {
  const result = {
    parsed: null,
    incomplete: [],
    errors: [],
  };

  if (!incompleteJsonString || typeof incompleteJsonString !== 'string') {
    result.errors.push('Invalid input: expected non-empty string');
    return result;
  }

  try {
    // First try to parse as-is in case it's already complete
    try {
      result.parsed = JSON.parse(incompleteJsonString);
      return result;
    } catch (e) {
      // Try auto-fixing common issues
      let fixedJson = incompleteJsonString.trim();

      // Fix 0: Smart conversion of single quotes to double quotes
      // Only convert single quotes that are used as JSON string delimiters, not apostrophes in content
      fixedJson = convertSingleQuotesToDouble(fixedJson);

      // Fix 1: Remove extra closing braces
      fixedJson = fixedJson.replace(/}+$/, '}');

      // Fix 2: Wrap multiple root objects in array
      if (fixedJson.includes('},{')) {
        // Check if it's multiple root objects by counting top-level braces
        const bracePairs = [];
        let depth = 0;
        let inString = false;
        let escaped = false;

        for (let i = 0; i < fixedJson.length; i++) {
          const char = fixedJson[i];

          if (escaped) {
            escaped = false;
            continue;
          }

          if (char === '\\' && inString) {
            escaped = true;
            continue;
          }

          if (char === '"') {
            inString = !inString;
            continue;
          }

          if (!inString) {
            if (char === '{') {
              if (depth === 0) bracePairs.push(i);
              depth++;
            } else if (char === '}') {
              depth--;
              if (depth === 0) bracePairs.push(i);
            }
          }
        }

        // If we have multiple complete objects at root level, wrap in array
        if (bracePairs.length > 2) {
          fixedJson = '[' + fixedJson + ']';
        }
      }

      // Try parsing the fixed JSON
      try {
        result.parsed = JSON.parse(fixedJson);
        return result;
      } catch (e2) {
        // Continue with incomplete parsing if fixes didn't work
      }
    }

    const parser = new IncompleteJSONParser(incompleteJsonString);
    const parseResult = parser.parse();

    result.parsed = parseResult.value;
    result.incomplete = parseResult.incomplete;
    result.errors = parseResult.errors;
  } catch (error) {
    result.errors.push(`Parsing failed: ${error.message}`);
  }

  return result;
}

class IncompleteJSONParser {
  constructor(jsonString) {
    // Use smart conversion that preserves apostrophes in content
    this.input = convertSingleQuotesToDouble(jsonString.trim());
    this.pos = 0;
    this.incomplete = [];
    this.errors = [];
    this.path = [];
  }

  parse() {
    this.skipWhitespace();

    if (this.pos >= this.input.length) {
      this.errors.push('Empty input');
      return { value: null, incomplete: this.incomplete, errors: this.errors };
    }

    // Auto-fix: Handle multiple root objects by parsing them into an array
    const values = [];
    let parsedCount = 0;

    while (this.pos < this.input.length) {
      this.skipWhitespace();

      if (this.pos >= this.input.length) break;

      const value = this.parseValue();
      values.push(value);
      parsedCount++;

      this.skipWhitespace();

      if (this.pos >= this.input.length) break;

      // Check for comma indicating multiple root objects
      if (this.input[this.pos] === ',') {
        this.pos++; // Skip comma
        continue; // Parse next object
      } else {
        break; // No more objects
      }
    }

    // Return single value if only one was parsed, otherwise return array
    const finalValue = parsedCount === 1 ? values[0] : values;

    // Convert undefined values to null recursively
    const convertUndefinedToNull = (obj) => {
      if (obj === undefined) return null;
      if (obj === null) return obj;

      // Convert string representations of undefined to null
      if (typeof obj === 'string' && obj.trim() === 'undefined') return null;

      if (typeof obj !== 'object') return obj;

      if (Array.isArray(obj)) {
        return obj.map(convertUndefinedToNull);
      }

      const result = {};
      for (const [key, value] of Object.entries(obj)) {
        result[key] = convertUndefinedToNull(value);
      }
      return result;
    };

    return { value: convertUndefinedToNull(finalValue), incomplete: this.incomplete, errors: this.errors };
  }

  parseValue() {
    this.skipWhitespace();

    if (this.pos >= this.input.length) {
      this.addIncomplete('value');
      return null;
    }

    const char = this.input[this.pos];

    switch (char) {
      case '{':
        return this.parseObject();
      case '[':
        return this.parseArray();
      case '"':
        return this.parseString();
      case 't':
      case 'f':
        return this.parseBoolean();
      case 'n':
        return this.parseNull();
      default:
        if (this.isDigit(char) || char === '-') {
          return this.parseNumber();
        }
        // Handle incomplete values at end of input
        return this.handleIncompleteValue();
    }
  }

  parseObject() {
    const obj = {};
    this.pos++; // Skip '{'
    this.skipWhitespace();

    if (this.pos >= this.input.length) {
      this.addIncomplete('object');
      return obj;
    }

    if (this.input[this.pos] === '}') {
      this.pos++;
      return obj;
    }

    while (this.pos < this.input.length) {
      this.skipWhitespace();

      if (this.pos >= this.input.length) {
        this.addIncomplete('object');
        break;
      }

      // Parse key
      if (this.input[this.pos] !== '"') {
        // Handle incomplete key
        const incompleteKey = this.parseIncompleteString();
        if (incompleteKey !== null) {
          this.path.push(incompleteKey);
          this.addIncomplete('object-key');
          this.path.pop();
        }
        break;
      }

      const key = this.parseString();
      if (key === null) break;

      this.path.push(key);
      this.skipWhitespace();

      if (this.pos >= this.input.length) {
        this.addIncomplete('object-value');
        obj[key] = null;
        this.path.pop();
        break;
      }

      if (this.input[this.pos] !== ':') {
        this.addIncomplete('object');
        this.path.pop();
        break;
      }

      this.pos++; // Skip ':'
      this.skipWhitespace();

      if (this.pos >= this.input.length) {
        this.addIncomplete('object-value');
        obj[key] = null;
        this.path.pop();
        break;
      }

      // Parse value
      const value = this.parseValue();
      obj[key] = value;
      this.path.pop();

      this.skipWhitespace();

      if (this.pos >= this.input.length) {
        this.addIncomplete('object');
        break;
      }

      if (this.input[this.pos] === '}') {
        this.pos++;
        break;
      }

      if (this.input[this.pos] === ',') {
        this.pos++;
        continue;
      }

      // If we get here, something's wrong but we'll try to continue
      break;
    }

    return obj;
  }

  parseArray() {
    const arr = [];
    this.pos++; // Skip '['
    this.skipWhitespace();

    if (this.pos >= this.input.length) {
      this.addIncomplete('array');
      return arr;
    }

    if (this.input[this.pos] === ']') {
      this.pos++;
      return arr;
    }

    let index = 0;
    while (this.pos < this.input.length) {
      this.path.push(index);
      this.skipWhitespace();

      if (this.pos >= this.input.length) {
        this.addIncomplete('array');
        this.path.pop();
        break;
      }

      const value = this.parseValue();

      // Auto-fix: Check for unclosed objects by detecting duplicate keys
      if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
        const keys = Object.keys(value);
        const uniqueKeys = [...new Set(keys)];
        if (keys.length !== uniqueKeys.length) {
          // Found duplicate keys, likely an unclosed object
          // Keep only the first occurrence of each key
          const fixedValue = {};
          const seenKeys = new Set();
          for (const key of keys) {
            if (!seenKeys.has(key)) {
              fixedValue[key] = value[key];
              seenKeys.add(key);
            }
          }
          arr.push(fixedValue);
        } else {
          arr.push(value);
        }
      } else {
        arr.push(value);
      }

      this.path.pop();
      index++;

      this.skipWhitespace();
      this.skipExtraClosingBraces(); // Auto-fix: Remove extra closing braces

      if (this.pos >= this.input.length) {
        this.addIncomplete('array');
        break;
      }

      if (this.input[this.pos] === ']') {
        this.pos++;
        break;
      }

      if (this.input[this.pos] === ',') {
        this.pos++;
        continue;
      }

      // Auto-fix: If we encounter a new object starting while expecting array continuation,
      // it likely means the previous object was missing a closing bracket
      if (this.input[this.pos] === '{') {
        // The previous object was likely unclosed, continue parsing the next object
        continue;
      }

      break;
    }

    return arr;
  }

  parseString() {
    if (this.pos >= this.input.length || this.input[this.pos] !== '"') {
      return this.parseIncompleteString();
    }

    this.pos++; // Skip opening quote
    let str = '';
    let escaped = false;

    while (this.pos < this.input.length) {
      const char = this.input[this.pos];

      if (escaped) {
        // Handle escape sequences properly
        switch (char) {
          case 'n':
            str += '\n';
            break;
          case 't':
            str += '\t';
            break;
          case 'r':
            str += '\r';
            break;
          case 'b':
            break;
          case 'f':
            str += '\f';
            break;
          case '"':
            str += '"';
            break;
          case '\\':
            str += '\\';
            break;
          case '/':
            str += '/';
            break;
          default:
            str += char;
            break;
        }
        escaped = false;
      } else if (char === '\\') {
        escaped = true;
      } else if (char === '"') {
        if (this.isRealClosingQuote()) {
          // This is likely the closing quote
          this.pos++; // Skip closing quote
          return str;
        } else {
          // This is an internal quote - treat it as regular content
          str += char;
        }
      } else {
        str += char;
      }

      this.pos++;
    }

    // Incomplete string
    this.addIncomplete('string');
    return str;
  }

  parseIncompleteString() {
    let str = '';

    while (this.pos < this.input.length) {
      const char = this.input[this.pos];
      if (char === ',' || char === '}' || char === ']' || char === ':') {
        break;
      }
      str += char;
      this.pos++;
    }

    if (str.length > 0) {
      this.addIncomplete('string');
      const cleanedStr = str.replace(/^"/, ''); // Remove leading quote if present

      // Convert string representations of undefined to null
      if (cleanedStr.trim() === 'undefined') {
        return null;
      }

      return cleanedStr;
    }

    return null;
  }

  parseNumber() {
    let numStr = '';

    while (this.pos < this.input.length) {
      const char = this.input[this.pos];
      if (this.isDigit(char) || char === '.' || char === '-' || char === '+' || char === 'e' || char === 'E') {
        numStr += char;
        this.pos++;
      } else {
        break;
      }
    }

    // Check if we reached end of input during number parsing
    if (this.pos >= this.input.length && numStr.length > 0) {
      const lastChar = numStr[numStr.length - 1];
      if (lastChar === '.' || lastChar === '-' || lastChar === '+' || lastChar === 'e' || lastChar === 'E') {
        this.addIncomplete('number');
      }
    }

    const num = Number.parseFloat(numStr);
    return isNaN(num) ? 0 : num;
  }

  parseBoolean() {
    const remaining = this.input.slice(this.pos);

    if (remaining.startsWith('true')) {
      this.pos += 4;
      return true;
    } else if (remaining.startsWith('false')) {
      this.pos += 5;
      return false;
    } else if ('true'.startsWith(remaining) || 'false'.startsWith(remaining)) {
      this.addIncomplete('boolean');
      this.pos = this.input.length;
      return !!remaining.startsWith('t');
    }

    return null;
  }

  parseNull() {
    const remaining = this.input.slice(this.pos);

    if (remaining.startsWith('null')) {
      this.pos += 4;
      return null;
    } else if ('null'.startsWith(remaining)) {
      this.addIncomplete('null');
      this.pos = this.input.length;
      return null;
    }

    return null;
  }

  handleIncompleteValue() {
    // Try to guess what kind of value this might be
    const remaining = this.input.slice(this.pos);

    if (remaining.match(/^[tf]/)) {
      return this.parseBoolean();
    } else if (remaining.match(/^n/)) {
      return this.parseNull();
    } else if (remaining.match(/^[\d-]/)) {
      return this.parseNumber();
    } else {
      // Treat as incomplete string
      return this.parseIncompleteString();
    }
  }

  skipWhitespace() {
    while (this.pos < this.input.length && /\s/.test(this.input[this.pos])) {
      this.pos++;
    }
  }

  isDigit(char) {
    return char >= '0' && char <= '9';
  }

  addIncomplete(type) {
    this.incomplete.push({
      path: this.getPathString(),
      type,
      position: this.pos,
    });
  }

  getPathString() {
    return this.path.length > 0 ? this.path.join('.') : 'root';
  }

  skipExtraClosingBraces() {
    // Skip any extra closing braces that might appear
    while (this.pos < this.input.length && this.input[this.pos] === '}') {
      this.pos++;
    }
  }

  isRealClosingQuote() {
    // Look ahead to see what follows this quote (after skipping whitespace)
    let lookAhead = this.pos + 1;
    while (lookAhead < this.input.length && /\s/.test(this.input[lookAhead])) {
      lookAhead++;
    }

    if (lookAhead >= this.input.length) {
      // End of input - this could be a real closing quote
      return true;
    }

    const nextChar = this.input[lookAhead];
    // Real closing quotes are followed by JSON syntax
    const isJsonSyntax = nextChar === ',' || nextChar === '}' || nextChar === ']' || nextChar === ':';

    return isJsonSyntax;
  }

  isInternalQuote(strSoFar) {
    // Rule 1: Natural language pattern - quote preceded by space
    if (strSoFar.length > 0 && strSoFar[strSoFar.length - 1] === ' ') {
      return true;
    }

    // Rule 2: HTML attribute pattern - detect if we're inside an HTML tag
    if (strSoFar.includes('<') && !strSoFar.includes('>')) {
      // We're inside an HTML tag, check if this looks like an attribute value
      const lastEqual = strSoFar.lastIndexOf('=');
      if (lastEqual !== -1) {
        const afterEqual = strSoFar.slice(lastEqual + 1);
        // If there's content after '=' but no closing '>', this is likely an HTML attribute
        if (afterEqual.length > 0 && !afterEqual.includes('>')) {
          return true;
        }
      }
    }

    // Rule 3: Inside an HTML tag with multiple attributes
    // Look for patterns like 'attribute="value" attribute="'
    if (strSoFar.includes('<')) {
      const lastOpenTag = strSoFar.lastIndexOf('<');
      const afterTag = strSoFar.slice(lastOpenTag);

      // Check if we have an unclosed tag with attributes
      if (!afterTag.includes('>') && afterTag.includes('=')) {
        return true;
      }
    }

    return false;
  }
}

export default parseIncompleteJSON;
